import { useState } from "react";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { EmptyState } from "../../components/EmptyState";
import { Spinner } from "../../components/Spinner";
import { ApiError, apiGet } from "../../lib/customFetch";

interface PingResponse {
  pong: boolean;
  tenantId: string;
}

type PingState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok" }
  | { status: "gone" }
  | { status: "failed" };

/**
 * Minimal feature-side der beviser hele kæden:
 * toggle → /api/features → nav-link → denne side → GET /api/ping.
 * 404 vises ærligt som "findes ikke" — det er inertheds-kontrakten.
 */
export function PingPage() {
  const [state, setState] = useState<PingState>({ status: "idle" });

  const sendPing = () => {
    setState({ status: "loading" });
    apiGet<PingResponse>("/api/ping")
      .then(() => setState({ status: "ok" }))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setState({ status: "gone" });
        } else {
          setState({ status: "failed" });
        }
      });
  };

  return (
    <Card
      title="Forbindelse"
      actions={
        <Button onClick={sendPing} disabled={state.status === "loading"}>
          Tjek forbindelse
        </Button>
      }
    >
      {state.status === "idle" && (
        <EmptyState
          title="Klar til forbindelsestjek"
          description="Kontrollér, at AppSmeden kan hente virksomhedens oplysninger."
        />
      )}
      {state.status === "loading" && <Spinner large />}
      {state.status === "ok" && (
        <EmptyState
          title="Forbindelsen virker"
          description="AppSmeden har kontakt til virksomhedens oplysninger."
        />
      )}
      {state.status === "gone" && (
        <EmptyState
          title="Forbindelsestjek er ikke slået til"
          description="Denne funktion er ikke tilgængelig for virksomheden."
        />
      )}
      {state.status === "failed" && (
        <EmptyState
          title="Kunne ikke tjekke forbindelsen"
          description="Tjek din internetforbindelse og prøv igen."
          cta={<Button onClick={sendPing}>Prøv igen</Button>}
        />
      )}
    </Card>
  );
}
